import { gql } from "@apollo/client";
import { IconButton, Skeleton } from "@mui/material";
import { addDays, startOfDay } from "date-fns";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useNavigate } from "react-router-dom";
import {
  coordinateToMapPoint,
  MapMarkerInput
} from "@caredna-marlin/map-wrapper";
import { default as GenericMapWrapper } from "@caredna-marlin/map-wrapper/google-map-wrapper";
import { useVisitLocationsQuery, VisitLocationsQuery } from "~/graphql/__generated__/graphql";
import { useParams } from "react-router-dom";
import PatientLocationSearchBox from "../route/findPatientLocation";
import { getVisitResourceLocation } from "@caredna-marlin/utils/locations";
import { useMapPatientInfo } from "~/state/map/mapPatientInfo";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { hasOverlap } from "@caredna-marlin/utils/client";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";

const findShift = (
  vr: VisitLocationsQuery["visitResource"][number],
  shifts: VisitLocationsQuery["shift"],
) => shifts.find(s => s.providerId === vr.provider?.id && hasOverlap(vr, s))

type TimeFormatters = {
  timeLabel: (value: string | Date) => string;
  timeRangeLabel: (start: string | Date, end?: string | null) => string;
};

function createMapMarkersFromVisitResources(
  visitResources: VisitLocationsQuery["visitResource"],
  shifts: VisitLocationsQuery["shift"],
  formatters: TimeFormatters
): MapMarkerInput[] {
  const markerInputList: MapMarkerInput[] = [];

  const activeInHomeResources = visitResources.filter(
    (a) =>
      a.visit?.eventStatus !== "canceled" && a.resourceRequirement?.inHome !== false
  );

  const resourcesByVisit: Record<string, typeof activeInHomeResources[number][]> = {};
  activeInHomeResources.forEach((vr) => {
    if (vr.visit && findShift(vr, shifts) && vr.provider) {
      const visitId = vr.visitId || `${vr.visit.patient?.id || 'no-patient'}-${vr.visit.addressCoordinates || 'no-coords'}-${vr.startTime}`;
      if (!resourcesByVisit[visitId]) {
        resourcesByVisit[visitId] = [];
      }
      resourcesByVisit[visitId].push(vr);
    }
  });

  type Visit = {
    visitId: string;
    resources: typeof activeInHomeResources[number][];
    startTime: string;
    endTime?: string | null;
    coordinate: string | null;
    patient: any;
    providerName: string;
    orderIndex: number;
  };

  const visits: Visit[] = Object.entries(resourcesByVisit).map(([visitId, resources]) => ({
    visitId,
    resources,
    startTime: resources[0].startTime,
    endTime: resources[0].endTime,
    coordinate: getVisitResourceLocation(resources[0], resources[0].provider!, findShift(resources[0], shifts)!),
    patient: resources[0].visit?.patient,
    providerName: resources.map(r => `${r.provider?.firstName} ${r.provider?.lastName}`).join(', '),
    orderIndex: 0
  })).filter(visit => visit.coordinate && visit.patient);

  visits.sort((a, b) => Date.parse(a.startTime) - Date.parse(b.startTime));
  visits.forEach((visit, index) => {
    visit.orderIndex = index + 1;
  });

  const visitsByLocation: Record<string, typeof visits> = {};
  visits.forEach(visit => {
    if (!visitsByLocation[visit.coordinate!]) {
      visitsByLocation[visit.coordinate!] = [];
    }
    visitsByLocation[visit.coordinate!].push(visit);
  });

  const buildPatientFullLabel = (patientVisits: Visit[]) => {
    if (patientVisits.length === 0) { return ""; }
    const patient = patientVisits[0].patient;
    const patientName = `${patient.firstName} ${patient.lastName}`;
    const sortedVisits = [...patientVisits].sort((a, b) => Date.parse(a.startTime) - Date.parse(b.startTime));
    const visitLines = sortedVisits.map((visit) => {
      const timeRange = formatters.timeRangeLabel(visit.startTime, visit.endTime);
      const providerName = visit.providerName || "Unknown";
      return `${visit.orderIndex}. ${timeRange} (${providerName})`;
    });
    return `${patientName}\n${visitLines.join('\n')}`;
  };

  Object.entries(visitsByLocation).forEach(([coordinate, locationVisits]) => {
    const point = coordinateToMapPoint(coordinate);
    if (!point) return;

    const patientColorMap = new Map<number, number>();
    let colorIndex = 0;
    locationVisits.forEach(visit => {
      const patientId = visit.patient?.id;
      if (patientId && !patientColorMap.has(patientId)) {
        patientColorMap.set(patientId, colorIndex++);
      }
    });

    if (locationVisits.length === 1) {
      const visit = locationVisits[0];
      const patientColorIndex = patientColorMap.get(visit.patient?.id) || 0;
      markerInputList.push({
        label: buildPatientFullLabel([visit]),
        timestamp: new Date(visit.startTime),
        highlighted: Date.parse(visit.startTime) > Date.now(),
        icon: "dot",
        point,
        colorIndex: patientColorIndex,
        patient: visit.patient
      });
      return;
    }

    const visitsByPatient = new Map<number, Visit[]>();
    locationVisits.forEach((visit) => {
      const patientId = visit.patient?.id;
      if (!patientId) { return; }
      if (!visitsByPatient.has(patientId)) {
        visitsByPatient.set(patientId, []);
      }
      visitsByPatient.get(patientId)!.push(visit);
    });

    // If multiple visits are all for one patient, use the single-circle marker
    if (visitsByPatient.size === 1) {
      const [patientId, patientVisits] = Array.from(visitsByPatient.entries())[0];
      const patientColorIndex = patientColorMap.get(patientId) || 0;
      markerInputList.push({
        label: buildPatientFullLabel(patientVisits),
        timestamp: new Date(patientVisits[0].startTime),
        highlighted: locationVisits.some(v => Date.parse(v.startTime) > Date.now()),
        icon: "dot",
        point,
        colorIndex: patientColorIndex,
        patient: patientVisits[0].patient
      });
      return;
    }

    // Multiple patients at the same location: use multi-visit marker with deduped patients
    markerInputList.push({
      label: "",
      timestamp: new Date(locationVisits[0].startTime),
      highlighted: locationVisits.some(v => Date.parse(v.startTime) > Date.now()),
      icon: "dot",
      point,
      colorIndex: 0,
      patient: undefined,
      visitEntries: Array.from(visitsByPatient.entries()).map(([patientId, patientVisits]) => {
        const patient = patientVisits[0].patient;
        const patientName = `${patient.firstName} ${patient.lastName}`;
        const patientColorIndex = patientColorMap.get(patientId) || 0;
        const sortedVisits = [...patientVisits].sort((a, b) => Date.parse(a.startTime) - Date.parse(b.startTime));
        const latestVisit = sortedVisits[sortedVisits.length - 1];
        const providerNames = Array.from(new Set(sortedVisits.map((visit) => visit.providerName).filter(Boolean))).join(', ');

        return {
          scheduleIndex: sortedVisits[0].orderIndex,
          time: formatters.timeRangeLabel(sortedVisits[0].startTime, sortedVisits[0].endTime),
          displayName: patientName,
          fullLabel: buildPatientFullLabel(sortedVisits),
          timestamp: new Date(latestVisit.startTime),
          colorIndex: patientColorIndex,
          providerName: providerNames,
          patientId
        };
      })
    });
  });

  return markerInputList;
}

export default function VisitLocationIndexView() {
  const navigate = useNavigate();
  const { date } = useParams();
  const orgConfig = useOrgConfig();
  const { timeLabel, timeRangeLabel } = useZonedDateFormat();
  const parsedDate = date ? new Date(date) : new Date();
  const filterStart = startOfDay(parsedDate);
  const filterEnd = addDays(filterStart, 1);
  const {mapPatientInfo, loadingPatientInfo, setMapPatientId} = useMapPatientInfo();

  const { data, loading } = useVisitLocationsQuery({
    variables: {
      start: filterStart.toISOString(),
      end: filterEnd.toISOString()
    },
  });

  const mapWidth = "75vw";
  const mapHeight = "60vh";
  const toDate = (value: string | Date) => (value instanceof Date ? value : new Date(value));
  const formatters: TimeFormatters = {
    timeLabel: (value) => timeLabel(toDate(value), true),
    timeRangeLabel: (start, end) => {
      if (!end) { return timeLabel(toDate(start), true); }
      return timeRangeLabel(toDate(start), toDate(end), false, false);
    }
  };
  const markerInputs: MapMarkerInput[] = data?.visitResource 
    ? createMapMarkersFromVisitResources(data.visitResource, data.shift, formatters)
    : [];

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <IconButton size="small" onClick={() => { navigate(-1) }} color="primary">
          <ArrowBackIosIcon />
        </IconButton>
        <h1>In-home Visit Locations</h1>
      </div>
      <PatientLocationSearchBox mapPatientInfo={mapPatientInfo} setMapPatientId={setMapPatientId} loadingPatientInfo={loadingPatientInfo}/>
      {loading || !data ? (
        <Skeleton variant="rectangular" width={mapWidth} height={mapHeight} />
      ) : (
        <GenericMapWrapper
          mapsApiKey={orgConfig.GOOGLE_MAPS_WEB_API_KEY}
          displayRoute={false}
          clusterMarkers={true}
          height={mapHeight}
          width={mapWidth}
          markerInputs={markerInputs}
          trackingPoints={[]}
          targetLocation={mapPatientInfo?.location}
          onPatientClick={(patientId: number) => {
            navigate(`/patients/${patientId}`);
          }}
        />
      )}
    </>
  );
}

gql`
  query VisitLocations($start: String!, $end: String!)
  @permission(requires: useWebApp)
  {
    visitResource(where: { startTime: { gte: $start, lt: $end }}) {
      startTime
      endTime
      visitId
      addressCoordinates
      resourceRequirement {
        inHome
      }
      provider {
        id
        firstName
        lastName
        addressCoordinates
      }
      visit {
        addressCoordinates
        eventStatus
        patient {
          id
          firstName
          lastName
        }
      }
    }

    shift(where: { startTime: { gte: $start, lt: $end }}) {
      startTime
      endTime
      providerId
      addressCoordinates
      useShiftCenterDriveTimes
      shiftCenter {
        addressCoordinates
      }
    }
  }
`;
